export function intersectBy<TSource, TOther, TKey>(
    source: TSource[],
    other: TOther[],
    sourceKeySelector: (item: TSource) => TKey,
    otherKeySelector: (item: TOther) => TKey
): TSource[] {
    // Create a Set of keys from the 'other' array for fast O(1) lookups
    const otherKeys = new Set(other.map(otherKeySelector));

    // Filter the source array, keeping only items whose key is in the otherKeys Set
    return source.filter((item) => {
        const key = sourceKeySelector(item);
        return otherKeys.has(key);
    });
}

/**
 * Checks whether two arrays have set equality: the same unique values regardless of order.
 * This order-independent comparison performs deduplication through `Set`; duplicate values
 * are ignored, and undefined arrays are treated as empty arrays.
 *
 * @template T The type of values being compared.
 * @param dataTable1 The first array or data table to compare.
 * @param dataTable2 The second array or data table to compare.
 * @returns `true` when both arrays represent the same set of values; otherwise, `false`.
 *
 * @example
 * AreSameSets(['Approved', 'Pending'], ['Pending', 'Approved', 'Approved']); // true
 */
export function AreSameSets<T>(dataTable1: T[] | undefined, dataTable2: T[] | undefined): boolean {
    const set1 = new Set(dataTable1 ?? []);
    const set2 = new Set(dataTable2 ?? []);

    return set1.size === set2.size && [...set1].every((status) => set2.has(status));
}
